import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { clientDetailsRouteUrl, links } from 'utils/featureToggles'
import { hasPermission, hasSomePermissions } from 'utils/permissionsValidator'

import { UpdateClientStatus } from '../../../../api/Clients/Types/UpdateClientStatus'
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal'
import { successToast, ToastContext } from '../../../../components/Toast/Toast'
import { ConfirmationContext } from '../../../../utils/ConfirmationContext'
import { ClientTab } from '../Navigation/ClientNavigation'
import ClientDetailsBankAccountsPage from './ClientDetailsBankAccounts/ClientDetailsBankAccountsPage'
import ClientDetailsContactPage from './ClientDetailsContact/ClientDetailsContactPage'
import { ClientDetailsCorporatePage } from './ClientDetailsCorporate/ClientDetailsCorporatePage'
import ClientDetailsDashboardPage from './ClientDetailsDashboard/ClientDetailsDashboardPage'
import ClientDetailsDocuments from './ClientDetailsDocument/ClientDetailsDocument'
import { DocumentPreviewModal } from './ClientDetailsDocument/DocumentPreviewModal'
import ClientDetailsFundingSummaryPage from './ClientDetailsFundingSummary/ClientDetailsFundingSummaryPage'
import ClientDetailsInterestRates from './ClientDetailsInterestRates'
import ClientDetailsNotes from './ClientDetailsNotes/ClientDetailsNotes'
import ClientDetailsOtherPage from './ClientDetailsOther/ClientDetailsOtherPage'
import ClientDetailsPOA from './ClientDetailsPOA'
import ClientDetailsStatisticsPage from './ClientDetailsStatistics/ClientDetailsStatisticsPage'
import { ClientDetailsSubscriptionsPage } from './ClientDetailsSubscriptions/ClientDetailsSubscriptionsPage'
import ClientDetailsTest, { TestsViewType } from './ClientDetailsTest'
import ClientDetailsTicket from './ClientDetailsTicket'
import ClientDetailsTransactionsPage from './ClientDetailsTransactions/ClientDetailsTransactionsPage'
import { ClientDetailsWallet } from './ClientDetailsWallet/ClientDetailsWallet'

const ResolveClientRoute = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const [requireConfirmation, setRequireConfirmation] = useContext(ConfirmationContext)
  const setToast = useContext(ToastContext)

  const clientsApiClient = useApiClient(ClientsApiClient)
  const { id, slug } = useParams<{ id: string; slug: ClientTab }>()

  const [documentPreviewModal, setDocumentPreviewModal] = useState<
    { id: string; name: string } | undefined
  >(undefined)

  const [crumbs, setCrumbs] = useState([{ title: 'Clients', link: links.recordsClients.url }])
  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    () => (id ? clientsApiClient.getClient(id) : Promise.resolve(null)),
    [id]
  )
  const { data: clientData, isLoading } = useFetchOne(callback)
  const permissions = auth?.permissions || []

  useEffect(() => {
    if (clientData) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: createTitle(clientData),
          link: clientDetailsRouteUrl(
            clientData,
            slug ? (matchesSlug(slug) ? slug : ClientTab.INDIVIDUAL) : ClientTab.INDIVIDUAL
          ),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [clientData]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateClientStatus = async (values: UpdateClientStatus) => {
    setRequireConfirmation({ payload: values })
    await clientsApiClient.updateClientStatus(values)
    setToast(successToast('Client status updated successfully.'))
    forceRefresh()
  }

  const handleConfirmUpdateClientStatus = async () => {
    if (requireConfirmation?.payload) {
      await clientsApiClient.updateClientStatus({
        ...(requireConfirmation?.payload as UpdateClientStatus),
        skipBlockedStatusCheck: true,
      })
      setToast(successToast('Client status updated successfully.'))
      setRequireConfirmation(null)
      forceRefresh()
    }
  }

  const matchesSlug = (clientDetailsSlug?: ClientTab) => {
    if (!clientDetailsSlug) {
      return false
    }
    return (
      clientDetailsSlug === ClientTab.INDIVIDUAL ||
      clientDetailsSlug === ClientTab.CORPORATE ||
      clientDetailsSlug === ClientTab.WALLETS ||
      clientDetailsSlug === ClientTab.INTEREST_RATES ||
      clientDetailsSlug === ClientTab.STATISTICS ||
      clientDetailsSlug === ClientTab.BANK_ACCOUNTS ||
      clientDetailsSlug === ClientTab.TRANSACTIONS ||
      clientDetailsSlug === ClientTab.CLASSIFICATION ||
      clientDetailsSlug === ClientTab.DOCUMENTS ||
      clientDetailsSlug === ClientTab.NOTES ||
      clientDetailsSlug === ClientTab.TESTS ||
      clientDetailsSlug === ClientTab.TESTS_KYC ||
      clientDetailsSlug === ClientTab.IB_INFO ||
      clientDetailsSlug === ClientTab.TICKETS ||
      clientDetailsSlug === ClientTab.POA ||
      clientDetailsSlug === ClientTab.OTHER ||
      clientDetailsSlug === ClientTab.CONTACT ||
      clientDetailsSlug === ClientTab.FUNDING_SUMMARY ||
      clientDetailsSlug === ClientTab.KYC ||
      clientDetailsSlug === ClientTab.SUBSCRIPTIONS
    )
  }

  const innerRender = () => {
    if (!matchesSlug(slug)) {
      return <PageNotFound />
    }
    if (isLoading) {
      return <Spinner />
    }
    if (!clientData) {
      return <PageNotFound />
    }

    if (
      slug === ClientTab.INDIVIDUAL &&
      hasPermission(permissions, PermissionsEnum.CanViewClientInformation)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsDashboardPage
            data={clientData}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.CORPORATE &&
      hasPermission(permissions, PermissionsEnum.CanViewClientInformation)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsCorporatePage
            data={clientData}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.WALLETS &&
      hasPermission(permissions, PermissionsEnum.CanViewClientWalletsAccounts)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsWallet
            data={clientData}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.INTEREST_RATES &&
      hasPermission(permissions, PermissionsEnum.CanBrowseClientsInterestRates)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsInterestRates
            data={clientData}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.STATISTICS &&
      hasPermission(permissions, PermissionsEnum.CanViewClientAccountStatistics)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsStatisticsPage
            data={clientData}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.BANK_ACCOUNTS &&
      hasSomePermissions(permissions, [
        PermissionsEnum.CanViewClientAccounts,
        PermissionsEnum.CanBrowseTokenizedCardDetails,
      ])
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsBankAccountsPage
            data={clientData}
            setDocumentPreviewModal={setDocumentPreviewModal}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.TRANSACTIONS &&
      hasPermission(permissions, PermissionsEnum.CanViewClientAccountTransactions)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsTransactionsPage
            data={clientData}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.FUNDING_SUMMARY &&
      hasPermission(permissions, PermissionsEnum.CanViewFundingSummary)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsFundingSummaryPage
            data={clientData}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.DOCUMENTS &&
      hasPermission(permissions, PermissionsEnum.CanViewClientDocuments)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsDocuments
            data={clientData}
            forceRefresh={forceRefresh}
            setDocumentPreviewModal={setDocumentPreviewModal}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.NOTES &&
      hasPermission(permissions, PermissionsEnum.CanViewClientNotes)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsNotes
            data={clientData}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (slug === ClientTab.TESTS_KYC) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsTest
            defaultTestViewType={TestsViewType.KYC}
            data={clientData}
            updateClientStatus={updateClientStatus}
            forceRefresh={forceRefresh}
          />
        </React.Fragment>
      )
    }
    if (slug === ClientTab.TESTS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsTest
            defaultTestViewType={TestsViewType.Tests}
            data={clientData}
            updateClientStatus={updateClientStatus}
            forceRefresh={forceRefresh}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.TICKETS &&
      hasPermission(permissions, PermissionsEnum.CanViewClientTickets)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsTicket data={clientData} updateClientStatus={updateClientStatus} />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.POA &&
      hasPermission(permissions, PermissionsEnum.CanViewClientPowerOfAttorney)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsPOA
            data={clientData}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.OTHER &&
      hasPermission(permissions, PermissionsEnum.CanViewClientOtherInformation)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsOtherPage
            data={clientData}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (
      slug === ClientTab.CONTACT &&
      hasPermission(permissions, PermissionsEnum.CanViewClientContact)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsContactPage
            data={clientData}
            forceRefresh={forceRefresh}
            updateClientStatus={updateClientStatus}
          />
        </React.Fragment>
      )
    }
    if (slug === ClientTab.SUBSCRIPTIONS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <ClientDetailsSubscriptionsPage clientData={clientData} />
        </React.Fragment>
      )
    }
    return <PageNotFound />
  }

  return (
    <React.Fragment>
      {requireConfirmation?.code && (
        <ConfirmationModal
          yesButtonText='Yes, process'
          onYes={handleConfirmUpdateClientStatus}
          onCancel={() => setRequireConfirmation(null)}
          visible={!!requireConfirmation.code}
          renderTitle={
            <div>
              <p>
                Client has a balance in a
                <br />
                Wallet and on a Trading Account. Do you want to block the client?
              </p>
            </div>
          }
        />
      )}
      {documentPreviewModal && (
        <DocumentPreviewModal
          document={{ id: documentPreviewModal.id, name: documentPreviewModal.name }}
          onClose={() => setDocumentPreviewModal(undefined)}
        />
      )}
      <Loading isLoading={isLoading && !matchesSlug(slug)}>{innerRender()}</Loading>
    </React.Fragment>
  )
}

const createTitle = (clientData: ClientDetailedDto) => {
  const firstName = clientData.firstname || ''
  const middleName = clientData.middlename || ''
  const lastName = clientData.lastname || ''
  return clientData.companyDetails
    ? clientData.companyDetails.name
    : `${firstName} ${middleName} ${lastName}`
}

export default ResolveClientRoute
